import { useNavigate, useParams } from 'react-router-dom';
import { Activity, Clock, Fingerprint, Network, ShieldCheck } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Modal, ProgressBar, Table } from 'react-bootstrap';
import Switch from 'react-switch';
import { useOperatorsState } from '../../../services/OperatorsList';
import { WidgetChart } from '../../../components/widget-chart/WidgetChart';
import { ProgressList } from '../../../components/progress-list/ProgressList';
import { DarkButton } from '../../../components/operator-dashboard/dark-button/DarkButton';
import { useTranslation } from 'react-i18next';
import { getLanguage } from '../../../util/languages';
import './DetailsDevices.scss';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { BreakdownList } from '../../../components/breakdown-list/BreakdownList';
import { zodResolver } from '@hookform/resolvers/zod';

import { FormControl, FormSelect } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Pagination } from '../../../components/pagination/pagination';
import { format } from 'date-fns';
import { TableWidget } from '../../../components/table-widget/TableWidget';
import DeviceRulesCard from '../../../components/DeviceRulesCard/DeviceRulesCard';
import DeviceGroupsCard from '../../../components/DeviceGroupsCard/DeviceGroupsCard';
import { LoadingSpinner } from '../../../components/loading-spinner/LoadingSpinner';

type Props = {
  summaryData: any;
};

// `key` is a stable English id (React key + tone lookup); `label` is the translated text.
// `toneValue` carries the untranslated status value for getStatusTone.
type InfoItem = { key: string; label: string; value: any; toneValue?: any };

// Same display rules the shared BreakdownList cards used: empty (or the 'N/A' sentinel) -> localized N/A.
const formatValue = (value: any, notAvailable: string) => {
  if (value === null || value === undefined || value === '' || value === 'N/A') {
    return notAvailable;
  }
  return value;
};

// US date/time format, matching the previous card subtitle formatting.
const formatToUSTime = (dateString: string, locale: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return dateString;
  }
  return date.toLocaleString(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  });
};

// Maps boolean-ish status values to a design-system chip tone (null = plain mono value).
const getStatusTone = (key: string, value: any): 'mint' | 'danger' | 'neutral' | null => {
  if (value === null || value === undefined) return null;
  const v = String(value).toLowerCase();
  if (key === 'Protection Status') return v === 'protected' ? 'mint' : 'danger';
  if (key === 'Internet Status') return v === 'allowed' ? 'mint' : 'danger';
  if (key === 'MDM Enrolled') return v !== 'not applicable' && v !== 'n/a' ? 'mint' : 'neutral';
  return null;
};

const InfoGroup = ({ title, icon: Icon, items }: { title: string; icon: LucideIcon; items: InfoItem[] }) => {
  const { t } = useTranslation();
  const notAvailable = t('deviceDetails.common.notAvailable');
  return (
  <div className="dd-details-group">
    <div className="dd-details-group-hd">
      <span className="dd-details-group-icon" aria-hidden="true">
        <Icon size={16} strokeWidth={1.75} />
      </span>
      <h3 className="bd-h3">{title}</h3>
    </div>
    <dl className="dd-details-rows">
      {items.map((item) => {
        const tone = getStatusTone(item.key, item.toneValue ?? item.value);
        return (
          <div className="dd-details-row" key={item.key}>
            <dt className="dd-details-label">{item.label}</dt>
            <dd className="dd-details-value">
              {tone ? (
                <span className={`bd-chip ${tone === 'neutral' ? '' : tone}`}>
                  {tone !== 'neutral' && <span className="dot" />}
                  {formatValue(item.value, notAvailable)}
                </span>
              ) : (
                <span className="bd-mono bd-truncate" title={String(formatValue(item.value, notAvailable))}>
                  {formatValue(item.value, notAvailable)}
                </span>
              )}
            </dd>
          </div>
        );
      })}
    </dl>
  </div>
  );
};

export const DetailsDevices = (props: Props) => {
  const navigate = useNavigate();
  const { operatorList, loggedUserData } = useOperatorsState();
  const { deviceId } = useParams();
  const { t, i18n } = useTranslation();
  const notAvailable = t('deviceDetails.common.notAvailable');

  const [filterApplied, setFilterApplied] = useState<any>();
  const [devicesData, setDevicesData] = useState<any>();
  const [latestDeviceMetric, setLatestDeviceMetric] = useState<any>();

  // Convert deviceId to number and handle undefined case
  const numericDeviceId = deviceId ? parseInt(deviceId, 10) : 0;

  async function fetchApi() {
    // Use numericDeviceId for API calls
    const data = await axios.get(`/v2/op/device/${numericDeviceId}/device-info`);
    setDevicesData(data?.data);
    setFilterApplied(data?.data?.webFilter);

    const deviceMetrics = await axios.get(`/v2/op/device/${numericDeviceId}/latest-device-metrics`);
    setLatestDeviceMetric(deviceMetrics?.data);
  }

  useEffect(() => {
    if (numericDeviceId > 0) {
      fetchApi();
    }
  }, [numericDeviceId]);

  // Rest of your existing functions...
  function getAccountInformation() {
    return [
      { key: 'Hostname', label: t('deviceDetails.details.fields.hostname'), value: devicesData?.name },
      { key: 'Manufacturer', label: t('deviceDetails.details.fields.manufacturer'), value: devicesData?.description },
      { key: 'Device Type', label: t('deviceDetails.details.fields.deviceType'), value: devicesData?.deviceType?.key },
    ];
  }

  function getDateInformation() {
    return [
      { key: 'Router', label: t('deviceDetails.details.fields.router'), value: devicesData?.serialNumber },
      { key: 'MAC Address', label: t('deviceDetails.details.fields.macAddress'), value: devicesData?.macAddress },
      {
        key: 'IPv4 Address',
        label: t('deviceDetails.details.fields.ipv4Address'),
        value: devicesData?.ipAddress && devicesData?.ipAddress !== '' ? devicesData?.ipAddress : t('deviceDetails.details.values.notConnected'),
      },
    ];
  }

  function getShieldInformation() {
    return [
      {
        key: 'Protection Status',
        label: t('deviceDetails.details.fields.protectionStatus'),
        value: devicesData?.cyberShield ? t('deviceDetails.details.values.protected') : t('deviceDetails.details.values.notProtected'),
        toneValue: devicesData?.cyberShield ? 'protected' : 'not protected',
      },
      {
        key: 'Internet Status',
        label: t('deviceDetails.details.fields.internetStatus'),
        value: devicesData?.internetAllowed ? t('deviceDetails.details.values.allowed') : t('deviceDetails.details.values.notAllowed'),
        toneValue: devicesData?.internetAllowed ? 'allowed' : 'not allowed',
      },
      {
        key: 'MDM Enrolled',
        label: t('deviceDetails.details.fields.mdmEnrolled'),
        // Known MDM states are mapped to a label; anything else is shown raw
        value:
          devicesData?.mdmState && devicesData?.mdmState !== ''
            ? t(`deviceDetails.mdmStates.${devicesData.mdmState}`, { defaultValue: devicesData.mdmState })
            : t('deviceDetails.details.values.notApplicable'),
        toneValue: devicesData?.mdmState && devicesData?.mdmState !== '' ? devicesData?.mdmState : 'not applicable',
      },
    ];
  }

  function getLastMetricSample() {
    return [
      { key: 'Signal strength', label: t('deviceDetails.details.metricFields.signalStrength'), value: latestDeviceMetric?.latestMetric?.signal ? latestDeviceMetric?.latestMetric?.signal : 'N/A' },
      {
        key: 'Transmit bitrate',
        label: t('deviceDetails.details.metricFields.transmitBitrate'),
        value: latestDeviceMetric?.latestMetric?.txBitrate ? latestDeviceMetric?.latestMetric?.txBitrate : 'N/A',
      },
      {
        key: 'Transmit state',
        label: t('deviceDetails.details.metricFields.transmitState'),
        value: latestDeviceMetric?.latestMetric?.rxBitrateAverage ? latestDeviceMetric?.latestMetric?.rxBitrateAverage : 'N/A',
      },
      {
        key: 'Receive bitrate',
        label: t('deviceDetails.details.metricFields.receiveBitrate'),
        value: latestDeviceMetric?.latestMetric?.rxBitrate ? latestDeviceMetric?.latestMetric?.rxBitrate : 'N/A',
      },
      {
        key: 'RX since last connection',
        label: t('deviceDetails.details.metricFields.rxSinceLastConnection'),
        value: latestDeviceMetric?.latestMetric?.rxBytes ? latestDeviceMetric?.latestMetric?.rxBytes : 'N/A',
      },
      {
        key: 'TX since last connection',
        label: t('deviceDetails.details.metricFields.txSinceLastConnection'),
        value: latestDeviceMetric?.latestMetric?.txBytes ? latestDeviceMetric?.latestMetric?.txBytes : 'N/A',
      },
      {
        key: 'Sample taken',
        label: t('deviceDetails.details.metricFields.sampleTaken'),
        value: latestDeviceMetric?.latestMetric?.createdAt ? latestDeviceMetric?.latestMetric?.createdAt : 'N/A',
      },
    ];
  }

  const formSchema = z.object({
    filterApplied: z.enum(['', 'under12', 'EARLYTEEN', 'youngAdults', 'noShield']),
    startDate: z.string().optional(),
    startTime: z.string().optional(),
    endDate: z.string().optional(),
    endTime: z.string().optional(),
    deviceIdentifier: z.string().optional(),
    deviceType: z.string().optional(),
  });

  type FormInputs = z.infer<typeof formSchema>;

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormInputs>({
    resolver: zodResolver(formSchema),
  });

  let metricPartOne = getLastMetricSample().slice(0, 3)
  let metricPartTwo = getLastMetricSample().slice(3, 6)
  let metricSubtitle = getLastMetricSample().slice(6, 7)
  const key = metricSubtitle[0].key;
  const value = metricSubtitle[0].value;
  const label = metricSubtitle[0].label;

  const metricSample = `${label}: ${formatValue(value, notAvailable)}`

  // Show loading or error if deviceId is invalid
  if (!deviceId || numericDeviceId <= 0) {
    return (
      <section className="dd-details" id="cpe-details-section">
        <LoadingSpinner text={t('deviceDetails.details.loading')} />
      </section>
    );
  }

  const sampleTaken = value !== 'N/A' ? formatToUSTime(String(value), getLanguage(i18n.language).locale) : notAvailable;

  return (
    <section className="dd-details" id="cpe-details-section">
      <header className="dd-details-hd">
        <div className="dd-details-hd-text">
          <h2 className="bd-h2">{t('deviceDetails.details.title')}</h2>
          <span className="bd-meta">{t('deviceDetails.details.subtitle')}</span>
        </div>
      </header>

      <div className="dd-details-groups">
        <InfoGroup title={t('deviceDetails.details.groups.identity')} icon={Fingerprint} items={getAccountInformation()} />
        <InfoGroup title={t('deviceDetails.details.groups.network')} icon={Network} items={getDateInformation()} />
        <InfoGroup title={t('deviceDetails.details.groups.status')} icon={ShieldCheck} items={getShieldInformation()} />
      </div>

      <div className="dd-details-metrics">
        <div className="dd-details-metrics-hd">
          <div className="dd-details-group-hd">
            <span className="dd-details-group-icon" aria-hidden="true">
              <Activity size={16} strokeWidth={1.75} />
            </span>
            <h3 className="bd-h3">{t('deviceDetails.details.latestMetric')}</h3>
          </div>
          <span className="bd-chip" title={metricSample}>
            <Clock size={12} strokeWidth={2} aria-hidden="true" />
            <span className="dd-details-chip-label">{label}:</span>
            <span className="bd-mono">{sampleTaken}</span>
          </span>
        </div>
        <div className="dd-details-metric-grid">
          {[...metricPartOne, ...metricPartTwo].map((item) => (
            <div className="dd-details-metric" key={item.key}>
              <span className="bd-field-label">{item.key}</span>
              <span className="bd-mono dd-details-metric-num bd-truncate" title={String(formatValue(item.value, notAvailable))}>
                {formatValue(item.value, notAvailable)}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="dd-details-cards filtersApplied">
        <div className="dd-details-card-slot">
          {/* Now deviceId is properly converted to number */}
          <DeviceRulesCard deviceId={numericDeviceId} countPerPage={2} />
        </div>
        <div className="dd-details-card-slot">
          <DeviceGroupsCard
            deviceId={numericDeviceId}
            countPerPage={2}
            hasSearch={true}
            hasCategoryFilter={true}
            title={t('deviceDetails.details.deviceGroups')}
          />
        </div>
      </div>
    </section>
  );
};
